import { exportLinkSchema, type PrivacyStatus, privacyStatusSchema } from "@app/protocol";
import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../config";
import { openExternal } from "../discord/handshake";
import { useStore } from "../store";

export interface PrivacyState {
  /** Null until the server answered (or when it cannot). */
  status: PrivacyStatus | null;
  busy: boolean;
  failed: boolean;
  requestDeletion: () => Promise<void>;
  cancelDeletion: () => Promise<void>;
  /** One switch a call: the name on the leaderboard, or usage measurement (decision 206). */
  setPrefs: (
    prefs: Partial<Pick<PrivacyStatus, "hideOnLeaderboard" | "measurement">>,
  ) => Promise<void>;
  /** Opens the one-time download link in the browser, outside Discord (decision 89). */
  download: () => Promise<void>;
}

const FAILED = Symbol("failed");

async function request(session: string, path: string, method: string, body?: unknown) {
  const headers: Record<string, string> = { Authorization: `Bearer ${session}` };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  try {
    const response = await fetch(`${API_BASE}/me${path}`, {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    return response.ok ? ((await response.json()) as unknown) : FAILED;
  } catch {
    return FAILED;
  }
}

/** The player's data rights from the server (review 0016, ADR 0031). */
export function usePrivacy(): PrivacyState {
  const session = useStore((s) => s.session);
  const sdk = useStore((s) => s.sdk);
  const [status, setStatus] = useState<PrivacyStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const call = useCallback(
    async (path: string, method: string, body?: unknown): Promise<unknown> => {
      if (session === null) return null;
      setBusy(true);
      const found = await request(session, path, method, body);
      setBusy(false);
      setFailed(found === FAILED);
      return found === FAILED ? null : found;
    },
    [session],
  );
  const take = useCallback((found: unknown) => {
    const parsed = privacyStatusSchema.safeParse(found);
    if (parsed.success) setStatus(parsed.data);
  }, []);

  useEffect(() => {
    void call("/privacy", "GET").then(take);
  }, [call, take]);

  return {
    status,
    busy,
    failed,
    requestDeletion: async () => take(await call("/deletion", "POST")),
    cancelDeletion: async () => take(await call("/deletion", "DELETE")),
    // The switch moves at once; a refusal puts it back.
    setPrefs: async (prefs) => {
      const before = status;
      if (before !== null) setStatus({ ...before, ...prefs });
      const found = privacyStatusSchema.safeParse(await call("/prefs", "PUT", prefs));
      setStatus(found.success ? found.data : before);
    },
    download: async () => {
      const link = exportLinkSchema.safeParse(await call("/export", "POST"));
      if (link.success) await openExternal(sdk, link.data.url);
    },
  };
}
